/**
 * useThemeProvider Hook
 * Applies the selected theme to the document element and manages theme lifecycle
 * Handles both simple theme switching and custom CSS variable injection
 */

import { useEffect } from 'react';
import { usePreferencesContext } from '../context/PreferencesContext';

interface ThemeVariables {
  [key: string]: string;
}

// Fallback theme UUIDs for when themeId is not set
const FALLBACK_THEME_IDS: Record<string, string> = {
  'light': '5e66fb46-29bc-4896-90e4-8b121e5d2f5e',
  'dark': 'b18d93f3-f6de-4e47-a8d8-f2c8ed5308bc',
  'midnight': 'f828d764-c2be-4b20-896b-cb738c45a063',
};

export function useThemeProvider() {
  const { preferences } = usePreferencesContext();

  // Apply theme to document on mount and when theme preference changes
  useEffect(() => {
    const theme = preferences.theme || 'light';
    
    // Apply theme as data attribute on html element (for CSS selectors)
    document.documentElement.setAttribute('data-theme', theme.toLowerCase());
    
    // Also apply as class for CSS selector flexibility
    document.documentElement.classList.remove('theme-light', 'theme-dark', 'theme-midnight');
    document.documentElement.classList.add(`theme-${theme.toLowerCase()}`);
    
    // Determine which theme ID to use (prefer explicit ID, fall back to known UUID)
    const themeId = preferences.themeId || FALLBACK_THEME_IDS[theme] || FALLBACK_THEME_IDS['light'];
    
    // Try to retrieve theme variables from sessionStorage by themeId
    const themeVarsJson = sessionStorage.getItem(`theme-vars-${themeId}`);
    
    if (themeVarsJson) {
      try {
        const themeVars: ThemeVariables = JSON.parse(themeVarsJson);
        
        // Apply all CSS variables to the root element
        Object.entries(themeVars).forEach(([varName, varValue]) => {
          const trimmedValue = typeof varValue === 'string' ? varValue.trim() : String(varValue);
          try {
            document.documentElement.style.setProperty(varName, trimmedValue);
          } catch (err) {
            console.warn(`[useThemeProvider] Failed to apply CSS variable ${varName}:`, err);
          }
        });
      } catch (err) {
        console.warn(`[useThemeProvider] ✗ Failed to parse theme variables for ${themeId}:`, err);
      }
    } else {
      // No custom variables — remove any inline overrides so CSS selectors take full effect
      document.documentElement.removeAttribute('style');
    }
    
    // Keep Angular MFE in sync — it reads APP_CurrentThemeId on mount to apply CSS variables
    localStorage.setItem('APP_CurrentThemeId', themeId);

    // Store in sessionStorage for quick access
    sessionStorage.setItem('currentTheme', theme.toLowerCase());
  }, [preferences.theme, preferences.themeId]);
}
